<template>
  <div :class="{ 'd-none': hidden }">
    <FormInputButton
      :disabled="disabled"
      :required="required"
      :invalid="invalid && dirty"
      :error="error"
      :label="label"
      @click="disabled || openCourseGroupModal()"
    >
      <AppTooltip
        v-if="value"
        placement="top"
        :disabled="!!value"
      >
        <div>{{ getDisplayName(selectedObject) || value }}</div>
        <template
          v-if="selectedObject.displayName"
          slot="tooltip"
        >
          <div>{{ selectedObject.displayName || "No Description" }}</div>
        </template>
      </AppTooltip>
      <div
        v-else
        class="text-muted"
      >Select a Group</div>
    </FormInputButton>
    <AppModal
      v-if="modals.courseGroup"
      :use-dimness="true"
      class="req__group__modal"
      @close="modals.courseGroup = false"
    >
      <template #modal-header>
        <AppHeading>
          Select Equivalent Course Group
        </AppHeading>
      </template>
      <template #modal-body>
        <div class="d-flex align-items-center mb-3">
          <div class="mr-2">
            <img
              v-if="isLoading"
              src="@/assets/loading-small.gif"
              alt="loading"
              style="width: 24px; height: 24px"
            />
            <AppIcon
              v-else
              class="align-middle"
              name="search"
            />
          </div>
          <FormText
            v-model="searchQuery"
            placeholder="Start typing..."
            class="flex-1"
            @input="searchCourseGroups"
          />
        </div>
        <AsyncPagination
          v-if="totalCount > pagination.limit"
          :listLength="totalCount"
          :per="pagination.limit"
          :currentPage="pagination.currentPage"
          buttonsClass="justify-content-end"
          @updatePagination="updatePagination"
        />
        <AppLoading v-if="isLoading" />
        <div v-else>
          <section
            v-for="courseGroup in courseGroups"
            :key="courseGroup._id"
            :class="[ 'cursor-pointer item-box w-100 my-2 p-2 rounded-5', courseGroup._id===value && 'border-primary']"
            @click="selectAndCloseModal(courseGroup)"
          >
            <div class="mb-2">
              <AppBadge :pill="true">Group: {{ courseGroup._id }}</AppBadge>
            </div>
            <div>
              <span class="text-muted">Description</span>
              <!-- The following line handles cases where course groups don't have a descriptionLong value,
              which came up in NAU data set -->
              <p>{{ courseGroup.description || "No Description" }}</p>
            </div>
          </section>
          <section v-if="!courseGroups.length">
            <div> No Equivalent Course Groups found.</div>
          </section>
        </div>
      </template>
      <template #modal-footer>
        <AppButton @click="selectNoneAndCloseModal">Select None</AppButton>
        <AppButton @click="modals.courseGroup = false">Close</AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import FormMixin from './FormMixin'
import FormInputButton from './FormInputButton'
import AsyncPagination from '@/components/AsyncPagination'
import FormText from './FormText'
import { mapGetters } from 'vuex'
import { getCourseGroups } from '@/api'
import _debounce from 'lodash/debounce'

export default {
  mixins: [ FormMixin ],
  components: {
    FormInputButton,
    AsyncPagination,
    FormText,
  },
  data () {
    return {
      searchQuery: null,
      currentPage: 1,
      limit: 25,
      totalCount: 0,
      isLoading: true,
      selectedObject: {},
      courseGroups: [],
      modals: {
        courseGroup: false,
      },
    }
  },
  computed: {
    ...mapGetters([ 'school' ]),
    pagination () {
      return {
        currentPage: this.currentPage,
        limit: this.limit,
        skip: (this.currentPage - 1) * this.limit,
      }
    },
  },
  watch: {
    pagination: {
      deep: true,
      async handler () {
        this.isLoading = true
        await this.fetchCourseGroups()
        this.isLoading = false
      },
    },
  },
  async  created () {
    this.isLoading = true
    await this.fetchCourseGroup(this.value)
    this.isLoading = false
  },
  methods: {
    debounceSearch: _debounce(async function () {
      await this.fetchCourseGroups()
      this.isLoading = false
    }, 500),
    async searchCourseGroups () {
      this.isLoading = true
      this.debounceSearch()
    },
    openCourseGroupModal () {
      this.modals.courseGroup = true
      this.isLoading = true
      this.fetchCourseGroups()
      this.isLoading = false
    },
    setCourseGroup (data) {
      this.selectedObject = data || {}
    },
    selectNoneAndCloseModal () {
      const group = { id: '' }
      this.update(group._id)
      this.modals.courseGroup = false
      this.setCourseGroup(group)
    },
    selectAndCloseModal (group = {}) {
      this.update(group._id)
      this.modals.courseGroup = false
      this.setCourseGroup(group)
    },
    updatePagination ({ toPage }) {
      this.currentPage = toPage
    },
    async fetchCourseGroup (val) {
      if (!val) return
      const params = {
        school: this.school,
        list: val,
      }
      const data = await getCourseGroups(params) || {}
      this.setCourseGroup(data[val] || data?.data?.[val])
    },
    async fetchCourseGroups () {
      const params = {
        school: this.school,
        includeCount: true,
        returnFields: 'id,code,description,descriptionLong',
        skip: this.pagination.skip,
        limit: this.pagination.limit,
      }
      if (this.searchQuery) params.searchQuery = this.searchQuery
      const { data = {}, totalCount = 0 } = await getCourseGroups(params) || {}
      this.totalCount = totalCount
      this.courseGroups = Object.values(data)
    },
    getDisplayName (group) {
      if (!group || !this.config.showDescription) return undefined
      const description = group.displayName || group.description || ''
      const code = group.code
      const doesDescriptionStartsWithCode = description.startsWith(code)
      return doesDescriptionStartsWithCode ? description : `${code} - ${description}`
    },
  },
}

</script>

<style scoped lang="scss">
@import "~@/css/mixins/transition";
.item-box {
  @include transition(background-color);
  border: 2px solid var(--gray);
}
.item-box:hover {
  background: var(--lighter-gray);
}

.req__group__modal /deep/ .modal-content {
  min-height: 80vh;
  max-height: 80vh;
}
</style>
